import { useState, useEffect } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { X, CheckCircle } from "lucide-react";
import { EDUCATION_PROGRAMS_LIST } from "../lib/educationPrograms";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PROGRAM_OPTIONS = [
  ...EDUCATION_PROGRAMS_LIST.map((p) => p.title),
  "Not sure — help me decide",
];

const initialForm = {
  firstName: "",
  lastName: "",
  email: "",
  phone: "",
  militaryStatus: "",
  programInterest: "",
  currentEducation: "",
  goal: "",
  timeline: "",
  bestTimeToContact: "",
  consent: false,
};

const inputCls =
  "w-full bg-slate-50 border border-slate-200 px-4 py-3 focus:border-slate-900 focus:ring-0 rounded-sm outline-none transition-colors";

export const EducationInquiryModal = ({ isOpen, onClose, defaultProgram = "" }) => {
  const [formData, setFormData] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState(null);

  useEffect(() => {
    if (isOpen && defaultProgram) {
      setFormData((prev) => ({ ...prev, programInterest: defaultProgram }));
    }
  }, [isOpen, defaultProgram]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.consent) {
      alert("Please provide consent to be contacted.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/education-inquiry`, formData);
      setStatus("success");
    } catch (err) {
      console.error("Education inquiry error:", err);
      setStatus("error");
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    onClose();
    setTimeout(() => {
      setFormData(initialForm);
      setStatus(null);
    }, 300);
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/70 z-[60] flex items-start md:items-center justify-center p-4 overflow-y-auto"
        onClick={handleClose}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          className="bg-white w-full max-w-2xl max-h-[92vh] overflow-y-auto my-4 rounded-sm"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="bg-[#0B2343] p-6 sticky top-0 z-10">
            <div className="flex justify-between items-start">
              <div>
                <h2 className="font-serif text-2xl font-bold text-white">Education Inquiry</h2>
                <p className="text-slate-400 text-sm mt-1">
                  Tell us about your goals and we'll follow up within 24–48 hours
                </p>
              </div>
              <button
                onClick={handleClose}
                className="text-slate-400 hover:text-white"
                data-testid="edu-close"
              >
                <X size={24} />
              </button>
            </div>
          </div>

          {status === "success" ? (
            <div className="p-8 text-center">
              <CheckCircle className="text-green-600 mx-auto mb-4" size={64} />
              <h3 className="font-serif text-2xl font-bold text-slate-900 mb-2">Thank You!</h3>
              <p className="text-slate-600 mb-6">
                Your inquiry has been received. We'll reach out to help you plan your next step.
              </p>
              <button
                onClick={handleClose}
                className="bg-slate-900 text-white px-8 py-3 font-medium hover:bg-slate-800 transition-colors"
                data-testid="edu-close-success"
              >
                Close
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field label="First Name" required>
                  <input type="text" name="firstName" required value={formData.firstName} onChange={handleChange} className={inputCls} data-testid="edu-firstname" />
                </Field>
                <Field label="Last Name" required>
                  <input type="text" name="lastName" required value={formData.lastName} onChange={handleChange} className={inputCls} data-testid="edu-lastname" />
                </Field>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field label="Email" required>
                  <input type="email" name="email" required value={formData.email} onChange={handleChange} className={inputCls} data-testid="edu-email" />
                </Field>
                <Field label="Phone" required>
                  <input type="tel" name="phone" required value={formData.phone} onChange={handleChange} className={inputCls} placeholder="(555) 123-4567" data-testid="edu-phone" />
                </Field>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field label="Military Status" required>
                  <select name="militaryStatus" required value={formData.militaryStatus} onChange={handleChange} className={inputCls} data-testid="edu-military">
                    <option value="">Select one</option>
                    <option value="Veteran">Veteran</option>
                    <option value="Active Duty">Active Duty</option>
                    <option value="National Guard/Reserves">National Guard/Reserves</option>
                    <option value="Dependent/Spouse">Dependent/Spouse</option>
                    <option value="Civilian">Civilian</option>
                  </select>
                </Field>
                <Field label="Current Education">
                  <select name="currentEducation" value={formData.currentEducation} onChange={handleChange} className={inputCls} data-testid="edu-current">
                    <option value="">Select one (optional)</option>
                    <option value="High School">High School</option>
                    <option value="Some College">Some College</option>
                    <option value="Associate's Degree">Associate's Degree</option>
                    <option value="Bachelor's Degree">Bachelor's Degree</option>
                    <option value="Graduate Degree">Graduate Degree</option>
                    <option value="Trade Certification">Trade Certification</option>
                  </select>
                </Field>
              </div>

              <Field label="Program Interest" required>
                <select name="programInterest" required value={formData.programInterest} onChange={handleChange} className={inputCls} data-testid="edu-program">
                  <option value="">Select a program</option>
                  {PROGRAM_OPTIONS.map((p) => (
                    <option key={p} value={p}>{p}</option>
                  ))}
                </select>
              </Field>

              <Field label="Your Goal" required>
                <textarea
                  name="goal"
                  required
                  rows={4}
                  value={formData.goal}
                  onChange={handleChange}
                  className={`${inputCls} resize-none`}
                  placeholder="What do you want to accomplish? (e.g., become an electrician, finish my bachelor's, transition into IT)"
                  data-testid="edu-goal"
                />
              </Field>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field label="Timeline">
                  <select name="timeline" value={formData.timeline} onChange={handleChange} className={inputCls} data-testid="edu-timeline">
                    <option value="">Select one (optional)</option>
                    <option value="Immediately">Immediately</option>
                    <option value="Within 3 months">Within 3 months</option>
                    <option value="Within 6 months">Within 6 months</option>
                    <option value="Within a year">Within a year</option>
                    <option value="Just exploring">Just exploring</option>
                  </select>
                </Field>
                <Field label="Best Time to Contact">
                  <input type="text" name="bestTimeToContact" value={formData.bestTimeToContact} onChange={handleChange} className={inputCls} placeholder="e.g., Weekdays after 5pm ET" data-testid="edu-time" />
                </Field>
              </div>

              <div className="bg-slate-100 p-4">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input type="checkbox" name="consent" checked={formData.consent} onChange={handleChange} className="w-4 h-4 mt-1" data-testid="edu-consent" />
                  <span className="text-sm text-slate-700">
                    <strong>Consent to Contact *</strong>
                    <br />I consent to be contacted by Noble Strategies Consulting regarding my education inquiry.
                  </span>
                </label>
              </div>

              <button
                type="submit"
                disabled={submitting}
                className="w-full bg-[#C89B3C] text-white px-8 py-4 font-medium hover:bg-[#a8852f] transition-colors disabled:opacity-50 mt-4"
                data-testid="edu-submit"
              >
                {submitting ? "Sending..." : "Submit Inquiry"}
              </button>

              {status === "error" && (
                <p className="text-red-500 text-sm text-center">
                  Something went wrong. Please try again or call 786-519-7988.
                </p>
              )}
            </form>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

const Field = ({ label, required, children }) => (
  <div>
    <label className="block text-sm font-medium text-slate-700 mb-2">
      {label} {required && "*"}
    </label>
    {children}
  </div>
);

export default EducationInquiryModal;
